import {
  passwordPolicySchema,
  Permission,
  ROLE_PERMISSIONS,
  SystemRole,
  type LoginInput,
  type PasswordPolicy,
  type ProductModule,
} from '@qualicloud/contracts';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  authApi,
  bootstrapSession,
  clearSession,
  onSessionLost,
  organizationsApi,
} from '@/lib/api';
import type { OrganizationView, SessionUser } from '@/lib/api/types';
import { FALLBACK_TIME_ZONE } from '@/lib/format/datetime';
import {
  AuthContext,
  type AuthContextValue,
  type AuthState,
  type OrganizationContextInfo,
} from './auth-context';

export { useAuth, useOrganizationTimeZone } from './auth-context';
export type {
  AuthContextValue,
  AuthState,
  OrganizationContextInfo,
} from './auth-context';

const DEFAULT_PASSWORD_POLICY: PasswordPolicy = passwordPolicySchema.parse({});

export function AuthProvider({ children }: { readonly children: ReactNode }) {
  const queryClient = useQueryClient();
  const [state, setState] = useState<AuthState>({
    status: 'booting',
    user: null,
    permissions: new Set<Permission>(),
    modules: new Set<ProductModule>(),
    organization: null,
    vendorAccess: false,
  });

  const loadOrganization = useCallback(
    async (
      organizationId: string,
      permissions: readonly Permission[],
    ): Promise<OrganizationContextInfo | null> => {
      // Sem ORG_MANAGE a leitura da organização inteira é 403 — e o `catch`
      // abaixo já tratava isso. Mas tentar mesmo assim disparava um 403 em CADA
      // navegação, no console e no log do gateway. Perguntar antes é o mesmo
      // resultado sem o ruído; o servidor continua sendo a autoridade.
      if (!permissions.includes(Permission.ORG_MANAGE)) return null;
      try {
        const org: OrganizationView = await organizationsApi.detail(organizationId);
        return {
          id: org.id,
          name: org.name,
          slug: org.slug,
          timeZone: org.timeZone || FALLBACK_TIME_ZONE,
          passwordPolicy: parsePolicy(org.passwordPolicy),
          allowSelfApproval: org.allowSelfApproval,
          documentExpiryWarningDays: org.documentExpiryWarningDays,
          recordRetentionYears: org.recordRetentionYears,
          part11CertificationLetterSentAt: org.part11CertificationLetterSentAt,
        };
      } catch {
        // Sem ORG_MANAGE o usuário comum não lê a organização inteira. A sessão
        // já traz nome, slug e fuso; a política de senha real é aplicada pelo
        // servidor de qualquer forma.
        return null;
      }
    },
    [],
  );

  const applySession = useCallback(
    async (
      user: SessionUser,
      permissions: readonly Permission[],
      /*
       * `modules` é OPCIONAL e o default é lista vazia — nunca "todos".
       *
       * Uma resposta antiga do servidor, em circulação durante um deploy, chega
       * sem o campo. Com default vazio, o menu esconde módulos que o cliente
       * tem: ele reclama no mesmo dia e o problema some na renovação seguinte.
       * Com default "todos", o menu mostraria telas que o gateway recusa com
       * 402 — o usuário clicaria e receberia um erro que ninguém sabe explicar.
       *
       * É a mesma decisão do claim do token, pelo mesmo motivo: falhar para o
       * lado que produz reclamação.
       */
      modules: readonly ProductModule[] = [],
    ) => {
      const organization = await loadOrganization(user.organizationId, permissions);
      setState({
        status: 'authenticated',
        user,
        permissions: new Set(permissions),
        modules: new Set(modules),
        organization: organization ?? {
          id: user.organizationId,
          name: user.organizationName,
          slug: user.organizationSlug,
          timeZone: user.timeZone || FALLBACK_TIME_ZONE,
          passwordPolicy: DEFAULT_PASSWORD_POLICY,
          allowSelfApproval: false,
          documentExpiryWarningDays: 30,
          recordRetentionYears: 10,
          part11CertificationLetterSentAt: null,
        },
        vendorAccess: user.role === SystemRole.PLATFORM_ADMIN,
      });
    },
    [loadOrganization],
  );

  const reset = useCallback(() => {
    clearSession();
    queryClient.clear();
    setState({
      status: 'anonymous',
      user: null,
      permissions: new Set<Permission>(),
      modules: new Set<ProductModule>(),
      organization: null,
      vendorAccess: false,
    });
  }, [queryClient]);

  /*
   * Recuperação de sessão no carregamento da página.
   *
   * O access token vive só em memória e morre com a aba — esse é o preço de não
   * usar localStorage. O refresh token, em cookie httpOnly, é o que permite
   * recarregar sem deslogar: aqui ele é trocado por um access token novo.
   */
  useEffect(() => {
    let cancelled = false;

    void (async () => {
      const recovered = await bootstrapSession();
      if (cancelled) return;

      if (!recovered) {
        setState((current) => ({ ...current, status: 'anonymous' }));
        return;
      }

      try {
        const profile = await authApi.profile();
        if (cancelled) return;
        await applySession(
          {
            id: profile.user.id,
            username: profile.user.username,
            displayName: profile.user.displayName,
            jobTitle: profile.user.jobTitle,
            role: profile.role,
            organizationId: profile.organizationId,
            // Vêm do próprio `/auth/me`. Antes eram preenchidos com string
            // vazia e com o fuso de fallback, então depois de um F5 o nome da
            // organização sumia do cabeçalho e as datas reguladas saíam no fuso
            // errado.
            organizationSlug: profile.organizationSlug,
            organizationName: profile.organizationName,
            timeZone: profile.timeZone || FALLBACK_TIME_ZONE,
            mustChangePassword: profile.user.mustChangePassword,
          },
          profile.permissions,
          profile.modules,
        );
      } catch {
        if (!cancelled) reset();
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [applySession, reset]);

  // O cliente HTTP avisa quando um 401 não pôde ser recuperado.
  useEffect(() => onSessionLost(reset), [reset]);

  const login = useCallback(
    async (input: LoginInput): Promise<SessionUser> => {
      const response = await authApi.login(input);
      await applySession(response.user, response.permissions, response.modules);
      return response.user;
    },
    [applySession],
  );

  const logout = useCallback(async () => {
    try {
      await authApi.logout();
    } finally {
      // Mesmo que o servidor falhe, o token local some: sessão pendurada numa
      // estação compartilhada é risco maior do que um refresh órfão no banco.
      reset();
    }
  }, [reset]);

  const refreshProfile = useCallback(async () => {
    const profile = await authApi.profile();
    setState((current) =>
      current.user
        ? {
            ...current,
            permissions: new Set(profile.permissions),
            user: {
              ...current.user,
              displayName: profile.user.displayName,
              jobTitle: profile.user.jobTitle,
              mustChangePassword: profile.user.mustChangePassword,
            },
          }
        : current,
    );
  }, []);

  const can = useCallback(
    (permission: Permission) => state.permissions.has(permission),
    [state.permissions],
  );

  /*
   * Conveniência de TELA, e nunca autorização.
   *
   * Quem impede de verdade é o gateway, com o claim assinado do token. Este
   * conjunto veio de `/auth/me` e serve para não desenhar um menu que leva a um
   * 402.
   */
  const hasModule = useCallback(
    (module: ProductModule) => state.modules.has(module),
    [state.modules],
  );

  const canAny = useCallback(
    (...permissions: readonly Permission[]) =>
      permissions.some((permission) => state.permissions.has(permission)),
    [state.permissions],
  );

  const setMustChangePassword = useCallback((value: boolean) => {
    setState((current) =>
      current.user ? { ...current, user: { ...current.user, mustChangePassword: value } } : current,
    );
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      ...state,
      login,
      logout,
      refreshProfile,
      can,
      canAny,
      hasModule,
      setMustChangePassword,
    }),
    [state, login, logout, refreshProfile, can, canAny, hasModule, setMustChangePassword],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

function parsePolicy(raw: unknown): PasswordPolicy {
  const parsed = passwordPolicySchema.safeParse(raw);
  return parsed.success ? parsed.data : DEFAULT_PASSWORD_POLICY;
}

/** Permissões nominais de um papel — usado apenas para EXIBIR o que ele concede. */
export function permissionsOfRole(role: SystemRole): readonly Permission[] {
  return ROLE_PERMISSIONS[role];
}
